import { timezoneOptions } from '@app/features/users/components/UserForm/form.config';
import type { FieldGroup } from '@app/types/form.types';
import type { TFunction } from 'i18next';

/**
 * The doctor's own account form.
 *
 * A MIRROR of the backend's rule, not a second opinion on it.
 * `reject_admin_only_user_fields` lets a non-admin change only
 * SELF_UPDATABLE_FIELDS on their own record - first name, last name, avatarUrl,
 * locale and timezone - and refuses the whole request, with a 403, if any other
 * field is present. So anything outside that list is rendered `disabled` here
 * and left out of the payload; showing it enabled would offer an edit the server
 * throws away.
 *
 * `email` is shown because an account without a visible address reads as broken,
 * and disabled because changing it is what the account signs in AS. `avatarUrl`
 * and `locale` are self-updatable but deliberately absent: there is no avatar
 * upload in this product, and locale is already the language switcher's - a
 * second control for it would be two places to set one thing.
 *
 * TIMEZONE IS THE ONLY EDITABLE FIELD. The server would take the doctor's own
 * first and last name too; the office keeps them anyway, so they are shown
 * disabled and left out of the payload. That is a product decision, not a
 * permission - which is why the two lists below differ from the backend's.
 */

/**
 * The keys this form may send. The save handler builds its payload from exactly
 * these, so a field added to the config above without being added here is
 * displayed and never saved - and a field added here that the server refuses
 * fails the whole save, which is why the two lists are kept beside each other.
 */
export const SELF_EDITABLE_FIELDS = ['timezone'] as const;

export const getDoctorProfileFields = (t: TFunction): FieldGroup[] => [
  {
    fields: [
      {
        name: 'firstName',
        label: t('users-form-first-name'),
        type: 'text',
        width: 'half',
        disabled: true,
        tooltip: t('doctor-profile-office-readonly-tooltip'),
      },
      {
        name: 'lastName',
        label: t('users-form-last-name'),
        type: 'text',
        width: 'half',
        disabled: true,
        tooltip: t('doctor-profile-office-readonly-tooltip'),
      },
      {
        name: 'email',
        label: t('users-form-email'),
        type: 'text',
        width: 'half',
        disabled: true,
        tooltip: t('doctor-profile-email-readonly-tooltip'),
      },
      {
        name: 'timezone',
        label: t('users-form-timezone'),
        type: 'select',
        width: 'half',
        options: timezoneOptions,
        componentProps: {
          showSearch: true,
          optionFilterProp: 'label',
          filterOption: (input: string, option?: { label?: string }) =>
            (option?.label || '').toLowerCase().includes(input.toLowerCase()),
        },
      },
    ],
  },
];
